import { useMemo, useState, type Key } from "react";
import {
  Modal,
  Table,
  Typography,
  Button,
  Pagination,
  Space,
  Checkbox,
} from "antd";
import { ProjectRelatedTabs } from "@/features/project-related-tabs";
import type {
  DocumentPackage,
  TabVariants,
} from "@/features/supervision-cards-list";
import { EmailPushIcon } from "@/shared/assets/svg/icons";
import { Chipper } from "@shared/ui";
import { SupervisionNotificationsAction } from "../supervision-notifications-action";
import { SupervisionNotificationsFilter } from "../supervision-notifications-filter";
import "./link-messages-modal.scss";

const { Text } = Typography;

interface LinkMessagesModalProps {
  open: boolean;
  onClose: () => void;
  data: DocumentPackage[];
}

export const LinkMessagesModal = ({
  open,
  onClose,
  data,
}: LinkMessagesModalProps) => {
  const [activeTab, setActiveTab] = useState<TabVariants>("incoming");
  const [currentFilter, setCurrentFilter] = useState<
    "all" | "archive" | "important"
  >("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

  const filtered = useMemo(() => {
    return data.filter((pkg) => {
      const tabOk = pkg.tab === activeTab;
      const statusOk = currentFilter === "all" || pkg.status === currentFilter;
      const searchOk =
        !search || pkg.title.toLowerCase().includes(search.toLowerCase());
      return tabOk && statusOk && searchOk;
    });
  }, [data, activeTab, currentFilter, search]);

  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  const pageData = filtered.slice(start, end);

  const columns = [
    {
      title: <span className="link-messages-modal__table-header-label">Документ подписан</span>,
      key: "documentSigned",
      render: (_: any, record: DocumentPackage) => {
        const row = record.table?.[0];
        if (!row) return "-";
        return (
          <div className="link-messages-modal__date-time">
            <span className="link-messages-modal__date">{row.documentSigned.date}</span>
            <span className="link-messages-modal__time">{row.documentSigned.time}</span>
          </div>
        );
      },
    },
    {
      title: <span className="link-messages-modal__table-header-label">Письмо отправлено</span>,
      key: "letterSent",
      render: (_: any, record: DocumentPackage) => {
        const row = record.table?.[0];
        if (!row) return "-";
        return (
          <div className="link-messages-modal__date-time">
            <span className="link-messages-modal__date">{row.letterSent.date}</span>
            <span className="link-messages-modal__time">{row.letterSent.time}</span>
          </div>
        );
      },
    },
  ];

  const tabCounts = useMemo(
    () => ({
      incoming: data.filter((p) => p.tab === "incoming").length,
      outgoing: data.filter((p) => p.tab === "outgoing").length,
      processing: data.filter((p) => p.tab === "processing").length,
    }),
    [data],
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={920}
      bodyStyle={{ maxHeight: 922, overflow: "hidden" }}
      title={
        <div>
          <div className="link-messages-modal__title">
            Связать сообщения
          </div>
          <Text type="secondary">
            Выберите сообщения с которыми хотите установить связь для документа
            и Получение замечаний о регистрации титульного листа
          </Text>
        </div>
      }
    >
      <div className="link-messages-modal">
        <div className="link-messages-modal__header">
          <div className="link-messages-modal__tabs">
            <ProjectRelatedTabs
              tabs={["incoming", "outgoing", "processing"]}
              tabsTranslate={[
                `Входящие ${tabCounts.incoming}`,
                `Исходящие ${tabCounts.outgoing}`,
                `На обработку ${tabCounts.processing}`,
              ]}
              activeTab={activeTab}
              setActiveTab={(tab: string) => {
                setActiveTab(tab as TabVariants);
                setPage(1);
              }}
            />
          </div>

          <div className="link-messages-modal__filters">
            <SupervisionNotificationsAction
              filterVariantsArray={["all", "archive", "important"]}
              currentFilter={currentFilter}
              updateFilter={(f) => {
                setCurrentFilter(f);
                setPage(1);
              }}
              openModal={() => {}}
            />
            <SupervisionNotificationsFilter
              isFullWidth
              searchValue={search}
              onSearchChange={setSearch}
            />
          </div>
        </div>

        <div className="link-messages-modal__table-scroll">
          <div className="table-container">
            <Table
              className="link-messages-modal__table"
              rowKey={(row) => row.id}
              dataSource={pageData}
              columns={[
                {
                  title: "",
                  key: "check",
                  width: 40,
                  render: (_: any, record: DocumentPackage) => (
                    <Checkbox
                      checked={selectedRowKeys.includes(record.id)}
                      onChange={(e) => {
                        const exists = selectedRowKeys.includes(record.id);
                        if (e.target.checked && !exists)
                          setSelectedRowKeys([...selectedRowKeys, record.id]);
                        if (!e.target.checked && exists)
                          setSelectedRowKeys(
                            selectedRowKeys.filter((k) => k !== record.id),
                          );
                      }}
                    />
                  ),
                },
                {
                  title: "Название",
                  dataIndex: "title",
                  key: "title",
                  render: (_: any, record: DocumentPackage) => (
                    <div className="link-messages-modal__title-cell">
                      <div className="link-messages-modal__title-chips">
                        <EmailPushIcon />
                        {record.chippers.map((chipper) => (
                          <Chipper
                            key={`chipper-${chipper.text}`}
                            backgroundColor={
                              chipper.backgroundColor ||
                              "rgba(119, 119, 119, 1)"
                            }
                          >
                            {chipper.text}
                          </Chipper>
                        ))}
                      </div>

                      <span className="selectable-link">{record.title}</span>
                    </div>
                  ),
                },
                ...columns,
              ]}
              pagination={false}
            />
          </div>
        </div>

        <div className="link-messages-modal__footer">
          <Pagination
            current={page}
            total={filtered.length}
            pageSize={pageSize}
            showSizeChanger
            showTotal={(total, range) => `${range[0]}-${range[1]} из ${total}`}
            pageSizeOptions={["10", "20", "50", "100"]}
            onChange={(p, s) => {
              setPage(p);
              if (s && s !== pageSize) {
                setPageSize(s);
                setPage(1);
              }
            }}
          />

          <Space size={12}>
            <Text type="secondary">Выбрано: {selectedRowKeys.length}</Text>
            <Button
              type="primary"
              disabled={!selectedRowKeys.length}
              onClick={() => onClose()}
            >
              Связать с выбранными
            </Button>
          </Space>
        </div>
      </div>
    </Modal>
  );
};
